import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { drapcodeIcon } from '../../../common/custom/icon-library';

const ThinkingSelector = ({ value, options, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const list = options;
  const selected = list.find((level) => level.id === value) || list[0];

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleSelect = (thinkingId) => {
    if (onChange) onChange(thinkingId);
    setOpen(false);
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((prev) => !prev);
        }}
        title={
          selected
            ? `Reasoning effort: ${selected.label} — ${selected.description || ''}`
            : 'Select thinking'
        }
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          height: '30px',
          padding: '0 10px',
          borderRadius: '999px',
          border: '1px solid #e2e8f0',
          background: open ? '#f1f5f9' : '#ffffff',
          color: '#334155',
          fontSize: '12px',
          fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        <i
          className={`wb ${drapcodeIcon('thinking')}`}
          aria-hidden="true"
          style={{ fontSize: '13px', lineHeight: 1, color: '#64748b' }}
        />
        <span>{selected ? selected.label : 'Thinking'}</span>
        <i
          className={`fa fa-chevron-${open ? 'up' : 'down'}`}
          aria-hidden="true"
          style={{ fontSize: '9px', color: '#64748b' }}
        />
      </button>

      {open && !disabled && (
        <div
          role="listbox"
          aria-label="Select reasoning effort"
          style={{
            position: 'absolute',
            bottom: '36px',
            left: 0,
            width: '280px',
            maxWidth: '82vw',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)',
            overflow: 'hidden',
            zIndex: 30,
          }}
        >
          <div
            style={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
              color: '#94a3b8',
              padding: '10px 12px 4px',
            }}
          >
            Reasoning effort
          </div>
          <div style={{ maxHeight: '260px', overflowY: 'auto', padding: '6px' }}>
            {list.map((level) => {
              const isSelected = selected && selected.id === level.id;
              return (
                <button
                  key={level.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(level.id);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    width: '100%',
                    textAlign: 'left',
                    border: '0',
                    borderRadius: '8px',
                    padding: '8px 10px',
                    background: isSelected ? '#eef2ff' : 'transparent',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#0f172a',
                      }}
                    >
                      {level.label}
                      {isSelected && (
                        <i
                          className="fa fa-check"
                          aria-hidden="true"
                          style={{ color: '#4f46e5', fontSize: '11px', marginLeft: 'auto' }}
                        />
                      )}
                    </span>
                    {level.description && (
                      <span
                        style={{
                          display: 'block',
                          fontSize: '11px',
                          color: '#64748b',
                          lineHeight: 1.4,
                        }}
                      >
                        {level.description}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

ThinkingSelector.propTypes = {
  value: PropTypes.string,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

ThinkingSelector.defaultProps = {
  value: '',
  onChange: null,
  disabled: false,
};

export default ThinkingSelector;
